import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export interface AdminFlag {
  key: string;
  enabled: boolean;
  enabledForTeams: string[];
  enabledForUsers: string[];
  description: string | null;
  updatedAt: string;
}

const FLAGS_KEY: QueryKey = ['admin', 'flags'];

export function useAdminFlags() {
  return useQuery({
    queryKey: FLAGS_KEY,
    queryFn: () => api<AdminFlag[]>('/api/admin/flags'),
  });
}

export interface CreateFlagInput {
  key: string;
  enabled?: boolean;
  description?: string;
  reason: string;
}

export function useCreateFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFlagInput) =>
      api<AdminFlag>('/api/admin/flags', { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}

export function useToggleFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled, reason }: { key: string; enabled: boolean; reason: string }) =>
      api<AdminFlag>(`/api/admin/flags/${key}`, { method: 'PATCH', body: { enabled, reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}

export function useDeleteFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, reason }: { key: string; reason: string }) =>
      api<void>(`/api/admin/flags/${key}`, { method: 'DELETE', body: { reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}

export function useAddTeamOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flagKey, teamId, reason }: { flagKey: string; teamId: string; reason: string }) =>
      api<AdminFlag>(`/api/admin/flags/${flagKey}/teams/${teamId}`, { method: 'POST', body: { reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}

export function useRemoveTeamOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flagKey, teamId, reason }: { flagKey: string; teamId: string; reason: string }) =>
      api<AdminFlag>(`/api/admin/flags/${flagKey}/teams/${teamId}`, { method: 'DELETE', body: { reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}

export function useAddUserOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flagKey, userId, reason }: { flagKey: string; userId: string; reason: string }) =>
      api<AdminFlag>(`/api/admin/flags/${flagKey}/users/${userId}`, { method: 'POST', body: { reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}

export function useRemoveUserOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flagKey, userId, reason }: { flagKey: string; userId: string; reason: string }) =>
      api<AdminFlag>(`/api/admin/flags/${flagKey}/users/${userId}`, { method: 'DELETE', body: { reason } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FLAGS_KEY }),
  });
}
